/**
 * utils/authFetch.js
 *
 * Wraps fetch() with Firebase ID token in Authorization header.
 * Use this for EVERY call to the HealHub backend from React components.
 *
 * Usage:
 *   import { authFetch, getUserClaims } from '../utils/authFetch';
 *
 *   // GET request
 *   const data = await authFetch('/api/usage').then(r => r.json());
 *
 *   // POST request
 *   const res = await authFetch('/api/chat', {
 *     method: 'POST',
 *     body: JSON.stringify({ message: 'hello' }),
 *   });
 *
 * ─── RAZORPAY NOTE ────────────────────────────────────────────────────────
 * After a successful Razorpay payment, call:
 *   await auth.currentUser.getIdToken(true);  // forces token refresh
 * This ensures the new { paid: true } claim is picked up immediately.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://heal-hub-healing-3.onrender.com';

/**
 * Authenticated fetch — automatically adds Firebase ID token.
 * @param {string} endpoint - e.g. '/api/chat'
 * @param {RequestInit} options - standard fetch options
 */
export const authFetch = async (endpoint, options = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated. Please log in.');

  const token = await user.getIdToken(); // uses cached token, refreshes if near-expiry

  return fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type' : 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

/**
 * Get current user's custom claims (includes { paid: true/false }).
 * Uses cached result — only re-fetches if token has expired.
 * Call getIdToken(true) to force a refresh after a payment.
 */
export const getUserClaims = async () => {
  const user = auth.currentUser;
  if (!user) return { paid: false };
  try {
    const result = await user.getIdTokenResult();
    return {
      paid   : result.claims.paid === true,
      isAdmin: result.claims.isAdmin === true,
      uid    : user.uid,
      email  : user.email,
    };
  } catch {
    return { paid: false };
  }
};